import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const API_URL = '/api';

function Login() {
  const navigate = useNavigate();
  const [accountHash, setAccountHash] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const hash = localStorage.getItem('avalw_account_hash');
    const type = localStorage.getItem('avalw_account_type');
    if (hash) {
      if (type === 'home') {
        navigate('/home-dashboard');
      } else {
        navigate('/dashboard');
      }
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!accountHash.trim()) {
      setError('Please enter your Account Hash');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_hash: accountHash.trim() })
      });
      const data = await res.json();

      if (data.success) {
        localStorage.setItem('avalw_account_hash', accountHash.trim());
        localStorage.setItem('avalw_worker_token', data.data?.worker_token || '');
        const accountType = data.data?.account_type || 'home';
        localStorage.setItem('avalw_account_type', accountType);
        navigate(accountType === 'home' ? '/home-dashboard' : '/dashboard');
      } else {
        setError(data.error || 'Account not found');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    }

    setLoading(false);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Left Side - Branding */}
        <div className="login-branding">
          <div className="login-brand-content">
            <div className="login-logo">
              <svg viewBox="0 0 40 40" fill="none">
                <circle cx="20" cy="20" r="18" stroke="currentColor" strokeWidth="2" fill="none"/>
                <circle cx="20" cy="20" r="8" fill="currentColor"/>
                <circle cx="20" cy="8" r="3" fill="currentColor"/>
                <circle cx="20" cy="32" r="3" fill="currentColor"/>
                <circle cx="8" cy="20" r="3" fill="currentColor"/>
                <circle cx="32" cy="20" r="3" fill="currentColor"/>
              </svg>
              <span>AVALW</span>
            </div>
            <h1>Welcome to the Network</h1>
            <p>Join thousands of contributors powering privacy-focused search.</p>

            <div className="login-features">
              <div className="login-feature">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 22c4.97 0 9-4.03 9-9-4.97 0-9 4.03-9 9zM5.6 10.25c0 1.38 1.12 2.5 2.5 2.5.53 0 1.01-.16 1.42-.44l-.02.19c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5l-.02-.19c.4.28.89.44 1.42.44 1.38 0 2.5-1.12 2.5-2.5 0-1-.59-1.85-1.43-2.25.84-.4 1.43-1.25 1.43-2.25 0-1.38-1.12-2.5-2.5-2.5-.53 0-1.01.16-1.42.44l.02-.19C14.5 2.12 13.38 1 12 1S9.5 2.12 9.5 3.5l.02.19c-.4-.28-.89-.44-1.42-.44-1.38 0-2.5 1.12-2.5 2.5 0 1 .59 1.85 1.43 2.25-.84.4-1.43 1.25-1.43 2.25z"/>
                </svg>
                <div>
                  <strong>Eco-Friendly</strong>
                  <span>Save CO2 with every contribution</span>
                </div>
              </div>
              <div className="login-feature">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                </svg>
                <div>
                  <strong>Privacy First</strong>
                  <span>AES-256 encrypted cache</span>
                </div>
              </div>
              <div className="login-feature">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                </svg>
                <div>
                  <strong>Join Alliances</strong>
                  <span>Team up with other contributors</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="login-form-side">
          <div className="login-card">
            <div className="login-header">
              <h2>Sign In</h2>
              <p>Enter your Account Hash to continue</p>
            </div>

            {error && (
              <div className="login-error">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                {error}
              </div>
            )}

            <form onSubmit={handleLogin}>
              <div className="login-input-group">
                <label>Account Hash</label>
                <div className="login-input-wrapper">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                  </svg>
                  <input
                    type="text"
                    placeholder="ea81ba1a4626f575fafda4cf..."
                    value={accountHash}
                    onChange={(e) => setAccountHash(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>

              <button type="submit" className="login-btn primary" disabled={loading}>
                {loading ? (
                  <>
                    <span className="login-spinner"></span>
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
                    </svg>
                  </>
                )}
              </button>
            </form>

            <div className="login-footer">
              <Link to="/">Back to Home</Link>
              <span>|</span>
              <a href="https://docs.avalw.org" target="_blank" rel="noopener noreferrer">Documentation</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
